/**
 * The launch funnel (go-to-market blocker 7).
 *
 * Fourteen days of visitors per step and channel, and each step over the one
 * before it. Kept plain on purpose: a launch needs to see where people fall
 * out, and a chart would suggest a precision these counts do not have. The
 * rates are counts over counts — the page steps count daily pseudonyms and the
 * later steps count accounts, and nothing follows one person through — which
 * the page says once, above the numbers, where it is read.
 */
import { useQuery } from "@tanstack/react-query";
import { Filter } from "lucide-react";
import apiClient from "@/services/api/apiClient";

type Step =
  | "landing"
  | "pricing"
  | "register_view"
  | "signup"
  | "checkout_start"
  | "paid";

interface Funnel {
  from: string;
  to: string;
  steps: Step[];
  channels: string[];
  totals: Record<Step, number>;
  byChannel: Record<Step, Record<string, number>>;
  conversion: { from: Step; to: Step; rate: number | null }[];
}

const STEP_LABELS: Record<Step, string> = {
  landing: "Home page",
  pricing: "Pricing",
  register_view: "Sign-up page",
  signup: "Signed up",
  checkout_start: "Checkout started",
  paid: "Paid",
};

const percent = (rate: number | null) =>
  rate === null ? "—" : `${(rate * 100).toFixed(1)}%`;

export default function FunnelPage() {
  const funnel = useQuery({
    queryKey: ["staff", "funnel", 14],
    queryFn: async () => {
      const response = await apiClient.get<{ data: Funnel }>(
        "/staff/dashboard/funnel",
        { params: { days: 14 } },
      );
      return response.data.data;
    },
  });

  const data = funnel.data;
  // Only the channels anything arrived through, so the table stays readable.
  const channels = data
    ? data.channels.filter((channel) =>
        data.steps.some((step) => (data.byChannel[step]?.[channel] ?? 0) > 0),
      )
    : [];

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Filter className="h-5 w-5 text-sky-600" /> Launch funnel
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {data ? `${data.from} to ${data.to}, UTC.` : "The last 14 days, UTC."}{" "}
          Page steps count each visitor once a day; the later steps count
          accounts. Rates are one count over another, not people followed
          through. Visitors sending Global Privacy Control or Do Not Track are
          not counted.
        </p>
      </div>

      {funnel.isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {funnel.isError && (
        <p className="text-sm text-red-600">The funnel could not be loaded.</p>
      )}

      {data && (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table
              className="w-full text-left text-sm"
              data-testid="funnel-table"
            >
              <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Step</th>
                  {channels.map((channel) => (
                    <th key={channel} className="px-4 py-3 font-medium">
                      {channel}
                    </th>
                  ))}
                  <th className="px-4 py-3 font-medium">Total</th>
                  <th className="px-4 py-3 font-medium">From previous step</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.steps.map((step, index) => {
                  const rate =
                    index === 0
                      ? null
                      : (data.conversion.find((c) => c.to === step)?.rate ??
                        null);
                  return (
                    <tr key={step}>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {STEP_LABELS[step] ?? step}
                      </td>
                      {channels.map((channel) => (
                        <td
                          key={channel}
                          className="px-4 py-3 tabular-nums text-gray-700"
                        >
                          {data.byChannel[step]?.[channel] ?? 0}
                        </td>
                      ))}
                      <td className="px-4 py-3 font-semibold tabular-nums text-gray-900">
                        {data.totals[step] ?? 0}
                      </td>
                      <td className="px-4 py-3 tabular-nums text-gray-700">
                        {index === 0 ? "" : percent(rate)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {channels.length === 0 && (
            <p className="text-sm text-gray-500">
              Nothing recorded in this period yet.
            </p>
          )}
        </>
      )}
    </div>
  );
}
